import { useState, useCallback } from "react";
import {
  login as apiLogin,
  register as apiRegister,
  staffLogin as apiStaffLogin,
  logoutApi,
} from "@/api/endpoints";

interface StaffInfo {
  staffId: string;
  staffName: string;
  role: "waiter" | "cashier";
  venueId: string;
  venueName: string;
  venueSlug: string;
}

function getStoredStaffInfo(): StaffInfo | null {
  const raw = localStorage.getItem("staffInfo");
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function persistTokens(token: string, refreshToken?: string) {
  localStorage.setItem("token", token);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
}

export function useAuth() {
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem("token")
  );
  const [staffInfo, setStaffInfo] = useState<StaffInfo | null>(getStoredStaffInfo);

  const isAuthenticated = !!token;
  const isStaff = !!staffInfo;
  const staffRole = staffInfo?.role ?? null;
  const staffVenueId = staffInfo?.venueId ?? null;

  const login = useCallback(async (venueNumber: number, password: string) => {
    const data = await apiLogin(venueNumber, password);
    persistTokens(data.token, data.refreshToken);
    localStorage.removeItem("staffInfo");
    setToken(data.token);
    setStaffInfo(null);
    return data.user;
  }, []);

  const staffLogin = useCallback(async (venueNumber: number, pin: string) => {
    const data = await apiStaffLogin(venueNumber, pin);
    // Staff login uses a regular access token only — no refresh flow yet.
    localStorage.setItem("token", data.token);
    localStorage.removeItem("refreshToken");
    const info: StaffInfo = {
      staffId: data.staff.id,
      staffName: data.staff.name,
      role: data.staff.role as "waiter" | "cashier",
      venueId: data.venue.id,
      venueName: data.venue.name,
      venueSlug: data.venue.slug,
    };
    localStorage.setItem("staffInfo", JSON.stringify(info));
    setToken(data.token);
    setStaffInfo(info);
    return data;
  }, []);

  const register = useCallback(async (data: { name: string; email: string; phone: string; venueName: string; password: string }) => {
    const res = await apiRegister(data);
    persistTokens(res.token, res.refreshToken);
    localStorage.removeItem("staffInfo");
    setToken(res.token);
    setStaffInfo(null);
    return { user: res.user, venueNumber: res.venueNumber };
  }, []);

  const logout = useCallback(async () => {
    // Best-effort revoke server-side, then clear local state regardless.
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      try {
        await logoutApi(refreshToken);
      } catch {
        // Network or server error — proceed with local logout anyway.
      }
    }
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("staffInfo");
    setToken(null);
    setStaffInfo(null);
  }, []);

  return { isAuthenticated, isStaff, staffInfo, staffRole, staffVenueId, token, login, staffLogin, register, logout };
}
